<template>
  <el-container class="preview-container">
    <el-aside width="280px" class="preview-aside">
      <div class="aside-card paper-meta-box">
        <h3 class="meta-title">{{ paperInfo.title }}</h3>
        <div class="meta-item">科目：<span>{{ paperInfo.subjectName }}</span></div>
        <div class="meta-item">满分：<span>{{ totalPaperScore }} 分</span></div>
      </div>

      <div class="aside-card navigation-card">
        <h4 class="card-title">试卷大纲导航</h4>
        <div v-for="(section, sIndex) in sectionsList" :key="sIndex" class="nav-group">
          <div class="group-label">{{ section.sectionName }}</div>
          <div class="number-grid">
            <span
              v-for="q in section.flatQuestions"
              :key="q.questionId"
              class="number-item"
              :class="{ 'is-active': currentActiveId === q.questionId }"
              @click="scrollToQuestion(q.questionId)"
            >
              {{ q.number }}
            </span>
          </div>
        </div>
      </div>
    </el-aside>

    <el-main class="preview-main">
      <div v-if="loading" class="loading-box">
        <el-skeleton :rows="10" animated />
      </div>

      <div v-else>
        <div v-for="(section, sIndex) in sectionsList" :key="sIndex" class="section-group">
          <h3 class="section-header-title">
            {{ section.sectionName }} <span class="section-desc">（{{ section.sectionHint }}）</span>
          </h3>

          <div
            v-for="q in section.questions"
            :id="q.typeCode !== 5 ? 'q-' + q.questionId : undefined"
            :key="q.questionId"
            class="question-card"
            @mouseenter="q.typeCode !== 5 ? (currentActiveId = q.questionId) : null"
          >
            <template v-if="q.typeCode === 1">
              <div class="question-stem">
                <span class="q-badge">单选</span>
                <span class="q-number">{{ q.number }}.</span>
                <span class="q-score-highlight">（{{ q.score }}分）</span>
                <span class="q-text">{{ q.content }}</span>
              </div>
              <div class="question-body">
                <div v-for="opt in q.options" :key="opt.pos" class="option-preview-item" :class="{ 'is-correct-opt': opt.correct }">
                  <el-tag size="mini" :type="opt.correct ? 'success' : 'info'" class="opt-tag">
                    {{ opt.correct ? '正确答案' : '选项' }}
                  </el-tag>
                  <span class="opt-text">{{ opt.content }}</span>
                </div>
              </div>
            </template>

            <template v-else-if="q.typeCode === 2">
              <div class="question-stem">
                <span class="q-badge type-multi" style="background-color: #f5dab1; color: #e6a23c;">多选</span>
                <span class="q-number">{{ q.number }}.</span>
                <span class="q-score-highlight">（{{ q.score }}分）</span>
                <span class="q-text">{{ q.content }}</span>
              </div>
              <div class="question-body">
                <div v-for="opt in q.options" :key="opt.pos" class="option-preview-item" :class="{ 'is-correct-opt': opt.correct }">
                  <el-tag size="mini" :type="opt.correct ? 'success' : 'info'" class="opt-tag">
                    {{ opt.correct ? '正确答案' : '选项' }}
                  </el-tag>
                  <span class="opt-text">{{ opt.content }}</span>
                </div>
              </div>
            </template>

            <template v-else-if="q.typeCode === 3">
              <div class="question-stem">
                <span class="q-badge type-fill">填空</span>
                <span class="q-number">{{ q.number }}.</span>
                <span class="q-score-highlight">（{{ q.score }}分）</span>
                <span class="q-text" style="white-space: pre-wrap;">{{ q.content }}</span>
              </div>
              <div class="question-body">
                <div class="answer-analysis-box">
                  <div class="analysis-title">📌 参考答案：</div>
                  <div v-for="ans in q.options" :key="ans.pos" class="fill-answer-item">
                    第 <span class="fill-index">{{ ans.pos }}</span> 空：
                    <span class="fill-text">{{ ans.content }}</span>
                  </div>
                </div>
              </div>
            </template>

            <template v-else-if="q.typeCode === 4">
              <div class="question-stem">
                <span class="q-badge type-qa">问答</span>
                <span class="q-number">{{ q.number }}.</span>
                <span class="q-score-highlight">（{{ q.score }}分）</span>
                <span class="q-text" style="white-space: pre-wrap;">{{ q.content }}</span>
              </div>
              <div class="question-body">
                <div class="answer-analysis-box">
                  <div class="analysis-title">📌 参考答案：</div>
                  <div v-for="ans in q.options" :key="ans.pos" class="qa-answer-content" style="white-space: pre-wrap; margin-top: 5px;">
                    {{ ans.content }}
                  </div>
                </div>
              </div>
            </template>

            <template v-else-if="q.typeCode === 5">
              <div class="question-stem material-stem">
                <span class="material-tag">【组合题】<span class="q-score-highlight">（全题共 {{ q.score }} 分）</span></span>
                <div class="q-text material-content">{{ q.content }}</div>
              </div>

              <div class="sub-questions-container">
                <div
                  v-for="subQ in q.children"
                  :id="'q-' + subQ.questionId"
                  :key="subQ.questionId"
                  class="sub-question-item"
                  @mouseenter="currentActiveId = subQ.questionId"
                >

                  <template v-if="subQ.typeCode === 1">
                    <div class="question-stem sub-stem">
                      <span class="q-badge btn-mini">单选</span>
                      <span class="q-number">{{ subQ.number }}.</span>
                      <span class="q-score-highlight">（{{ subQ.score }}分）</span>
                      <span class="q-text">{{ subQ.content }}</span>
                    </div>
                    <div class="question-body">
                      <div v-for="opt in subQ.options" :key="opt.pos" class="option-preview-item" :class="{ 'is-correct-opt': opt.correct }">
                        <el-tag size="mini" :type="opt.correct ? 'success' : 'info'" class="opt-tag">
                          {{ opt.correct ? '参考答案' : '选项' }}
                        </el-tag>
                        <span class="opt-text">{{ opt.content }}</span>
                      </div>
                    </div>
                  </template>

                  <template v-else-if="subQ.typeCode === 2">
                    <div class="question-stem sub-stem">
                      <span class="q-badge btn-mini" style="background-color: #f5dab1; color: #e6a23c;">多选</span>
                      <span class="q-number">{{ subQ.number }}.</span>
                      <span class="q-score-highlight">（{{ subQ.score }}分）</span>
                      <span class="q-text">{{ subQ.content }}</span>
                    </div>
                    <div class="question-body">
                      <div v-for="opt in subQ.options" :key="opt.pos" class="option-preview-item" :class="{ 'is-correct-opt': opt.correct }">
                        <el-tag size="mini" :type="opt.correct ? 'success' : 'info'" class="opt-tag">
                          {{ opt.correct ? '参考答案' : '选项' }}
                        </el-tag>
                        <span class="opt-text">{{ opt.content }}</span>
                      </div>
                    </div>
                  </template>

                  <template v-else-if="subQ.typeCode === 3">
                    <div class="question-stem sub-stem">
                      <span class="q-badge type-fill btn-mini">填空</span>
                      <span class="q-number">{{ subQ.number }}.</span>
                      <span class="q-score-highlight">（{{ subQ.score }}分）</span>
                      <span class="q-text">{{ subQ.content }}</span>
                    </div>
                    <div class="question-body">
                      <div class="answer-analysis-box">
                        <div class="analysis-title">📌 参考答案：</div>
                        <div v-for="ans in subQ.options" :key="ans.pos" class="fill-answer-item">
                          第 <span class="fill-index">{{ ans.pos }}</span> 空：
                          <span class="fill-text">{{ ans.content }}</span>
                        </div>
                      </div>
                    </div>
                  </template>

                  <template v-else-if="subQ.typeCode === 4">
                    <div class="question-stem sub-stem">
                      <span class="q-badge type-qa btn-mini">问答</span>
                      <span class="q-number">{{ subQ.number }}.</span>
                      <span class="q-score-highlight">（{{ subQ.score }}分）</span>
                      <span class="q-text">{{ subQ.content }}</span>
                    </div>
                    <div class="question-body">
                      <div class="answer-analysis-box">
                        <div class="analysis-title">📌 参考答案：</div>
                        <div v-for="ans in subQ.options" :key="ans.pos" class="qa-answer-content" style="white-space: pre-wrap; margin-top: 5px;">
                          {{ ans.content }}
                        </div>
                      </div>
                    </div>
                  </template>

                </div>
              </div>
            </template>

          </div>
        </div>
      </div>
    </el-main>
  </el-container>
</template>

<script>
import { getPaper } from '@/api/exam'

export default {
  name: 'ExamPaperPreview',
  data() {
    return {
      loading: true,
      queryParams: {
        paperId: 0
      },
      currentActiveId: null,
      paperInfo: {},
      sectionsList: [],
      totalPaperScore: 0
    }
  },
  created() {
    const paperId = this.$route.query.paperId
    if (!paperId) {
      this.$message.error('参数缺失，无法加载测评页面！')
      return
    }

    this.queryParams.paperId = paperId
    this.getData()
  },
  methods: {
    getData() {
      this.loading = true
      getPaper(this.queryParams).then(resp => {
        if (resp.code === 0) {
          document.title = '试卷预览'
          const remoteData = resp.data

          this.paperInfo = {
            paperId: remoteData.paperId,
            title: remoteData.title,
            subjectId: remoteData.subjectId,
            subjectName: remoteData.subjectName || '通用科目',
            duration: remoteData.duration
          }

          let globalQuestionIndex = 1
          let calculatedTotalScore = 0

          this.sectionsList = remoteData.sections.map(sec => {
            const flatQuestions = []

            const processedQuestions = sec.questions.map(q => {
              if (q.answers && !q.options) {
                q.options = q.answers
              }
              calculatedTotalScore += (q.score || 0)

              if (q.typeCode !== 5) {
                q.number = globalQuestionIndex++
                flatQuestions.push({ questionId: q.questionId, number: q.number })
              } else if (q.typeCode === 5 && q.children) {
                q.children.forEach(subQ => {
                  if (subQ.answers && !subQ.options) {
                    subQ.options = subQ.answers
                  }
                  subQ.number = globalQuestionIndex++
                  flatQuestions.push({ questionId: subQ.questionId, number: subQ.number })
                })
              }
              return q
            })

            return {
              sectionId: sec.sectionId,
              sectionName: sec.sectionName,
              sectionHint: sec.sectionHint,
              questions: processedQuestions,
              flatQuestions: flatQuestions
            }
          })

          this.totalPaperScore = calculatedTotalScore
        } else {
          this.$message.warning(resp.msg)
        }
      }).catch(error => {
        this.$message.error(error.message)
      }).finally(() => {
        this.loading = false
      })
    },

    scrollToQuestion(qId) {
      this.currentActiveId = qId
      const targetEl = document.getElementById(`q-${qId}`)
      if (targetEl) {
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }
}
</script>

<style scoped>
.preview-container { height: 100vh; background-color: #f8fafc; }
.preview-aside { padding: 20px; display: flex; flex-direction: column; gap: 16px; border-right: 1px solid #e2e8f0; }
.aside-card { background: #ffffff; border-radius: 8px; padding: 18px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }

.paper-meta-box { background: #fff; border-top: 4px solid #3b82f6; }
.meta-title { margin: 0 0 12px 0; font-size: 16px; color: #1e293b; }
.meta-item { font-size: 13px; color: #64748b; margin-bottom: 6px; }
.meta-item span { color: #1e293b; font-weight: bold; }

.navigation-card { flex: 1; display: flex; flex-direction: column; overflow-y: auto; }
.card-title { margin: 0 0 14px 0; color: #1e293b; border-left: 3px solid #3b82f6; padding-left: 8px; font-size: 14px; }
.nav-group { margin-bottom: 16px; }
.group-label { font-size: 12px; color: #64748b; margin-bottom: 8px; font-weight: bold; }
.number-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; }
.number-item { height: 32px; display: flex; align-items: center; justify-content: center; border: 1px solid #cbd5e1; border-radius: 4px; font-size: 12px; color: #475569; cursor: pointer; transition: all 0.2s; }
.number-item:hover, .number-item.is-active { border-color: #3b82f6; background-color: #eff6ff; color: #3b82f6; font-weight: bold; }

.preview-main { padding: 20px 40px; height: 100%; overflow-y: auto; }
.section-group { margin-bottom: 30px; }
.section-header-title { font-size: 16px; color: #1e293b; margin-bottom: 14px; padding-bottom: 6px; border-bottom: 1px dashed #e2e8f0; }
.section-desc { font-size: 13px; color: #94a3b8; font-weight: normal; }

.question-card { background: #ffffff; border-radius: 8px; padding: 20px; margin-bottom: 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.02); border-left: 4px solid transparent; transition: all 0.2s; }
.question-card:hover { border-left-color: #3b82f6; box-shadow: 0 4px 12px rgba(0,0,0,0.05); }

.question-stem { font-size: 14px; color: #1e293b; font-weight: 500; line-height: 1.6; margin-bottom: 14px; display: flex; align-items: flex-start; }
.q-badge { background: #edf2f7; color: #4a5568; font-size: 11px; padding: 2px 6px; border-radius: 4px; margin-right: 8px; margin-top: 2px; flex-shrink: 0; }
.q-badge.type-fill { background: #eef2ff; color: #4f46e5; }
.q-badge.type-qa { background: #fff7ed; color: #c2410c; }
.btn-mini { padding: 1px 4px; font-size: 10px; }
.q-number { margin-right: 4px; font-weight: bold; }
.question-body { padding-left: 48px; }

.option-preview-item { padding: 10px 14px; border: 1px solid #e2e8f0; border-radius: 6px; margin-bottom: 8px; font-size: 13px; color: #334155; display: flex; align-items: center; gap: 12px; }
.is-correct-opt { background-color: #f0fdf4; border-color: #bbf7d0; color: #15803d; font-weight: bold; }
.opt-tag { flex-shrink: 0; }

.answer-analysis-box { background: #f8fafc; border-left: 3px solid #64748b; padding: 12px 16px; border-radius: 0 6px 6px 0; margin-top: 8px; }
.analysis-title { font-size: 13px; font-weight: bold; color: #334155; margin-bottom: 6px; }
.text-red { color: #ea580c; }
.grey-text { color: #64748b; font-size: 13px; }
.fill-answer-item { font-size: 13px; color: #334155; margin-bottom: 4px; }
.fill-index { font-weight: bold; color: #3b82f6; }
.fill-text { background: #e2e8f0; padding: 1px 8px; border-radius: 4px; margin-left: 6px; font-family: monospace; }

.material-stem { background-color: #f1f5f9; padding: 16px; border-radius: 6px; border: 1px solid #cbd5e1; }
.material-tag { color: #0f172a; font-weight: bold; display: block; margin-bottom: 8px; font-size: 13px; }
.material-content { font-size: 13px; line-height: 1.7; color: #334155; white-space: pre-wrap; }
.sub-questions-container { margin-top: 16px; padding-left: 16px; border-left: 2px solid #cbd5e1; }
.sub-question-item { margin-bottom: 24px; padding-bottom: 12px; border-bottom: 1px dashed #f1f5f9; }
.sub-question-item:last-child { border-bottom: none; margin-bottom: 0; }
.sub-stem { font-size: 13px; }
.loading-box { padding: 40px; background: #fff; border-radius: 8px; }

.q-score-highlight {
  color: #67C23A;      /* 醒目的橘黄色/警告色，也可以换成 Element 的主色调 #409EFF */
  font-weight: bold;   /* 加粗显示 */
  margin-left: 4px;
}
</style>
